import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, RefreshCw } from 'lucide-react';
import OnboardingTemplate from '../OnboardingTemplate';
import SkillsCore from '@/components/dashboard/profile/skills/SkillsCore';
import { generateBio } from '@/utils/bioGenerator';
import { useAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { DISCIPLINES } from '@/data/constants';
import { useToast } from '@/hooks/use-toast';

interface SkillsStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const SkillsStep: React.FC<SkillsStepProps> = ({ onNext, onPrevious }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const MAX_SKILLS = 6;
  const [skills, setSkills] = useState<string[]>([]);
  const [newSkill, setNewSkill] = useState('');
  const [profession, setProfession] = useState<string>('');
  const [bio, setBio] = useState('');
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  const [profileLanguages, setProfileLanguages] = useState<string[]>([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;

      setIsLoadingProfile(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('profession, skills, bio, languages')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching profile during onboarding skills step:', error);
          return;
        }

        if (data) {
          setProfession(data.profession || '');
          setSkills(Array.isArray(data.skills) ? data.skills.filter(Boolean) : []);
          setBio(data.bio || '');
          setProfileLanguages(Array.isArray(data.languages) ? data.languages.filter(Boolean) : []);
        }
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [user?.id]);

  // Stepper/progress bar content
  const step = 2;
  const totalSteps = 3;
  const stepperContent = (
    <div className="mb-8">
      <div className="flex items-center mb-4">
        {[...Array(totalSteps)].map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 mx-4 rounded transition-colors duration-300 ${i === 1 ? 'bg-orange-500' : 'bg-gray-200'}`}
            style={{ maxWidth: 180 }}
          />
        ))}
      </div>
      <div className="text-xs text-muted-foreground mb-2">Step {step} of {totalSteps}</div>
    </div>
  );

  // Skills handlers
  const handleAddSkill = () => {
    const trimmedSkill = newSkill.trim();
    if (!trimmedSkill) return;
    if (skills.some((skill) => skill.toLowerCase() === trimmedSkill.toLowerCase())) return;
    if (skills.length >= MAX_SKILLS) return;

    setSkills([...skills, trimmedSkill]);
    setNewSkill('');
  };
  const handleRemoveSkill = (index: number) => {
    setSkills(skills.filter((_, i) => i !== index));
  };

  // About Me handlers
  const handleGenerateBio = async () => {
    setIsGeneratingBio(true);
    try {
      // For onboarding, you may want to pass empty strings for missing fields
      const generated = await generateBio({
        name: null,
        profession,
        skills,
        experience: 0,
        education: '',
        languages: profileLanguages,
        location: ''
      });
      setBio(generated);
    } catch (error) {
      // Optionally handle error
    } finally {
      setIsGeneratingBio(false);
    }
  };

  const handleNext = async () => {
    if (!user?.id) return;
    const normalizedSkills = Array.from(
      new Set(skills.map((skill) => skill.trim()).filter(Boolean))
    );
    const { error } = await supabase
      .from('profiles')
      .update({
        profession: profession || null,
        skills: normalizedSkills,
        bio,
        onboarding_status: 'in_progress',
        onboarding_step: 3
      })
      .eq('id', user.id);
    if (error) {
      console.error('Error updating profile during onboarding skills step:', error);
      toast({
        title: 'Something went wrong',
        description: 'We could not save your skills. Please try again.',
        variant: 'destructive',
      });
      return;
    }
    onNext();
  };

  return (
    <OnboardingTemplate showStepper={true} stepperContent={stepperContent}>
      <h1 className="text-3xl font-bold mb-6">Skills</h1>
      {/* Profession Section */}
      <div className="mb-6">
        <div className="font-medium mb-2">Profession</div>
        <Select
          value={profession}
          onValueChange={setProfession}
          disabled={isLoadingProfile}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select your profession" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {DISCIPLINES.map((discipline) => (
              <SelectItem key={discipline.id} value={discipline.label}>
                {discipline.label}
              </SelectItem>
            ))}
            {profession && !DISCIPLINES.some((discipline) => discipline.label === profession) && (
              <SelectItem value={profession}>{profession}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <p className="mt-2 text-xs text-muted-foreground">
          Choose the role that best matches your work. This helps us suggest relevant skills.
        </p>
      </div>
      {/* Skills Section */}
      <div className="mb-6">
        <div className="font-medium mb-2">Skills</div>
        <SkillsCore
          skills={skills}
          newSkill={newSkill}
          profession={profession || null}
          onNewSkillChange={setNewSkill}
          onAddSkill={handleAddSkill}
          onRemoveSkill={handleRemoveSkill}
          maxSkills={MAX_SKILLS}
          showLabel={false}
          className="bg-white border rounded-lg p-4"
        />
      </div>
      {/* About Me Section */}
      <div className="mb-6">
        <div className="font-medium mb-2">About Me</div>
        <Textarea
          placeholder="Write a short bio about yourself..."
          className="h-32 mb-2"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Fill in your profile details to generate a personalized bio automatically
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateBio}
            disabled={isGeneratingBio}
            className="flex items-center gap-2"
          >
            {isGeneratingBio ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Generate Bio
          </Button>
        </div>
      </div>
      {/* Navigation Buttons */}
      <div className="flex flex-row gap-2 pt-4">
        <Button variant="outline" onClick={onPrevious} className="w-1/2">
          Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={skills.length === 0 || !profession || isLoadingProfile}
          className="w-1/2"
        >
          Next
        </Button>
      </div>
    </OnboardingTemplate>
  );
};

export default SkillsStep; 
